import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { Flag } from '@hivebrite/design-system/Flag';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import {
  linkControlledHoverEffect,
  scaleWithFont,
} from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { Link } from 'hb-react/frontoffice/components/Link';
import TopicActions from 'hb-react/shared/apps/TopicsIndexV2/components/TopicActions';
import TopicDescription from 'hb-react/shared/apps/TopicsIndexV2/components/TopicDescription';
import TopicDetail from 'hb-react/shared/apps/TopicsIndexV2/components/TopicDetail';
import TopicDetailsModal from 'hb-react/shared/apps/TopicsIndexV2/components/TopicDetailsModal';
import { PARAM_FOLLOWED_BY_USER } from 'hb-react/shared/apps/TopicsIndexV2/constants/queryParams';
import { MY_TOPICS } from 'hb-react/shared/apps/TopicsIndexV2/constants/types';
import { useTopicCardContext } from 'hb-react/shared/apps/TopicsIndexV2/contexts/TopicCardContext';
import { useTopicsIndexContext } from 'hb-react/shared/apps/TopicsIndexV2/contexts/TopicsIndexContext';
import useCustomTopicName from 'hb-react/shared/apps/TopicsIndexV2/hooks/useCustomTopicName';
import type { ITopic } from 'hb-react/shared/apps/TopicsIndexV2/types';

interface TopicCardWithoutContextProps {
  areActionsDisabled?: boolean;
  children: React.ReactNode;
  forceDefaultLink?: boolean;
  href: string;
  headingLevel?: number;
  isConnectedUser: boolean;
  setDisplayDetailsModal: (value: boolean) => void;
  target?: string;
  topic: ITopic;
}

export const TopicCardWithoutContext = ({
  areActionsDisabled = false,
  children,
  forceDefaultLink = false,
  href,
  headingLevel = undefined,
  isConnectedUser,
  setDisplayDetailsModal,
  target = undefined,
  topic,
}: TopicCardWithoutContextProps) => {
  const {
    approval_id: approvalId,
    cover_picture_small_url: coverPictureUrl,
    joined_at: joinedAt,
    name,
    restricted_access: restrictedAccess,
    typeTopic,
  } = topic;
  const requestIsPending = !!approvalId && joinedAt === null;
  const topicCustomName = useCustomTopicName();

  const isMobile = useIsMobileDevice();

  const handleOpenModal = () => {
    !areActionsDisabled && setDisplayDetailsModal(true);
  };

  const titleWrapperHeight = `${scaleWithFont(48, !isConnectedUser)}px`;

  const cardHeaderContent = () => (
    <>
      <CoverPicture>
        <ImageWrapper $backgroundImage={coverPictureUrl} />
        {restrictedAccess && (
          <Box position="absolute" top={2} left={2}>
            <Flag
              data-testid="private-group-label-wrapper"
              text={
                requestIsPending
                  ? s__('Topics|Pending request')
                  : s__('Topics|Private')
              }
              aria-hidden
            />
          </Box>
        )}
      </CoverPicture>
      <TitleWrapper
        as={headingLevel ? `h${headingLevel}` : 'div'}
        height={titleWrapperHeight}
      >
        <ClampedLinkWorkaround clamp={2}>{name}</ClampedLinkWorkaround>
      </TitleWrapper>
      {restrictedAccess && (
        <ScreenReadersContent>
          {requestIsPending
            ? s__('Topics|Join request is pending')
            : s__('Topics|Private %{groupsName}').i__({
                groupsName: topicCustomName,
              })}
        </ScreenReadersContent>
      )}
    </>
  );

  return (
    <Card
      px={0}
      pt={0}
      pb={6}
      width="100%"
      height="inherit"
      minHeight="100%"
      data-testid="topics-index-topic-card"
      tabIndex={-1}
    >
      <Box display="block">
        {typeTopic !== MY_TOPICS ? (
          <HoverWrapper
            $enableHover={!(areActionsDisabled || isMobile)}
            $enableLinkHoverEffect
            onClick={handleOpenModal}
            data-testid="topics-card-hover-clickable-wrapper"
          >
            {cardHeaderContent()}
          </HoverWrapper>
        ) : (
          <Link forceDefaultLink={forceDefaultLink} target={target} to={href}>
            <HoverWrapper
              $enableHover={!(areActionsDisabled || isMobile)}
              $enableLinkHoverEffect
            >
              {cardHeaderContent()}
            </HoverWrapper>
          </Link>
        )}
        <Box mt={2} height={`${scaleWithFont(36)}px`}>
          <TopicDetail topic={topic} />
        </Box>
      </Box>
      {children}
    </Card>
  );
};

const TopicCard = () => {
  const { isConnectedUser } = useTopicsIndexContext();
  const { href, forceDefaultLink, target, topic } = useTopicCardContext();

  const [displayDetailsModal, setDisplayDetailsModal] = useState(false);

  const { category_lane: categoryLane } = topic;

  return (
    <TopicCardWithoutContext
      headingLevel={3}
      isConnectedUser={isConnectedUser}
      href={href}
      forceDefaultLink={forceDefaultLink}
      setDisplayDetailsModal={setDisplayDetailsModal}
      target={target}
      topic={topic}
    >
      {categoryLane !== PARAM_FOLLOWED_BY_USER && (
        <>
          <TopicActions topic={topic} />
          <TopicDescription setDisplayDetailsModal={setDisplayDetailsModal} />
        </>
      )}
      {displayDetailsModal && (
        <TopicDetailsModal setDisplayDetailsModal={setDisplayDetailsModal} />
      )}
    </TopicCardWithoutContext>
  );
};

export default TopicCard;

const ImageWrapper = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['backgroundImage'].includes(prop),
})<{ $backgroundImage: string }>`
  background-image: ${({ $backgroundImage }) => `url(${$backgroundImage})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  height: 100%;
  width: 100%;
  transition: 0.5s ease transform;
  border-top-left-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
  border-top-right-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;

const TitleWrapper = styled(Box).attrs(() => ({
  font: 'h3-semibold',
  mt: '14px',
  mx: 4,
  display: 'inline-block',
}))``;

const getDefaultTitleHoverEffect = (theme) => css`
  &:hover ${TitleWrapper} {
    text-decoration: underline;
    color: ${theme.colors.primary};
  }
`;

const getLinkTitleHoverEffect = () => css`
  &:hover ${TitleWrapper} {
    ${linkControlledHoverEffect()}
  }
`;

const getCssHoverProps = (enableLinkHoverEffect, theme) => css`
  ${enableLinkHoverEffect
    ? getLinkTitleHoverEffect()
    : getDefaultTitleHoverEffect(theme)}
  &:hover ${ImageWrapper} {
    transform: scale(1.2);
  }
`;

const HoverWrapper = styled.div<{
  $enableHover: boolean;
  $enableLinkHoverEffect: boolean;
}>`
  ${({ theme, $enableHover = false, $enableLinkHoverEffect = false }) =>
    $enableHover && getCssHoverProps($enableLinkHoverEffect, theme)}
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
`;

// we need to add some space otherwise the last underline will be cropped
// on Safari this even needs to be 4px, but other browsers don't like it
const ClampedLinkWorkaround = styled(Dotdotdot)`
  padding: 2px;
  margin: -2px;

  @media not all and (min-resolution: 0.001dpcm) {
    & {
      padding: 4px;
      margin: -4px;
    }
  }
`;

const CoverPicture = styled(Box).attrs(() => ({
  overflow: 'hidden',
  width: '100%',
}))`
  aspect-ratio: 757/418;
  border-top-left-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
  border-top-right-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;
